/**
 * Auth flow endpoints used by the registration UI.
 *
 * /auth/refresh and /auth/logout live in lib/auth/authClient.ts because they
 * must bypass apiFetch's 401 interceptor (it would recurse on refresh).
 * Everything else flows through apiFetch so we get the cookie + access-token
 * plumbing for free.
 */
import { ApiError, apiFetch, apiJson } from '../api';
import type { AuthTokens, AuthUser } from '../auth/types';
import { setAccessToken } from '../auth/tokenStore';

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

export interface AuthSuccessResponse {
  status: number;
  data: {
    user: AuthUser;
    tokens: AuthTokens;
  };
}

export interface RequestOtpResponse {
  status: number;
  message: string;
  /** Present only when the API has detected a likely typo (status 422). */
  code?: 'email_typo_suspected';
  suggestion?: { email: string; domain: string };
  original?: string;
}

export type RequestOtpResult =
  | { kind: 'ok' }
  | { kind: 'typo'; suggested: string };

/**
 * POST /auth/request-otp.
 *
 * The API always responds 200 with a generic body to prevent email enumeration,
 * BUT it returns 422 with `code: 'email_typo_suspected'` + a suggested email
 * when it detects gmial.com / gnail.com / etc. The caller can re-submit with
 * `confirmedOriginal: true` to bypass the suggestion.
 */
export async function requestOtp(
  email: string,
  confirmedOriginal?: boolean,
): Promise<RequestOtpResult> {
  // Bypass apiFetch — request-otp is unauthenticated and we want the raw 422
  // body without 401-refresh plumbing in the way.
  if (!API_BASE) throw new ApiError(0, 'VITE_API_BASE_URL is not configured');
  const res = await fetch(`${API_BASE}/auth/request-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(confirmedOriginal ? { email, confirmedOriginal: true } : { email }),
  });
  const body = (await res.json().catch(() => ({}))) as RequestOtpResponse;
  if (res.status === 422 && body.code === 'email_typo_suspected' && body.suggestion) {
    return { kind: 'typo', suggested: body.suggestion.email };
  }
  if (!res.ok) {
    throw new ApiError(res.status, body.message ?? `request-otp failed (${res.status})`, body);
  }
  return { kind: 'ok' };
}

export interface VerifyOtpBody {
  email: string;
  code: string;
}

/**
 * POST /auth/verify-otp.
 *
 * On success the API sets the httpOnly refreshToken + gh_session indicator
 * cookies and returns the access/identity token pair. We push accessToken
 * into the in-memory tokenStore so subsequent apiFetch calls authenticate.
 *
 * NOTE — does NOT dispatch the gh:auth-refreshed event. The RegisterPage
 * owns the post-verify routing and decides when to ping AuthContext.refresh().
 */
export async function verifyOtp(body: VerifyOtpBody): Promise<AuthSuccessResponse['data']> {
  if (!API_BASE) throw new ApiError(0, 'VITE_API_BASE_URL is not configured');
  const res = await fetch(`${API_BASE}/auth/verify-otp`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const parsed = (await res.json().catch(() => ({}))) as
    | AuthSuccessResponse
    | { status: number; message: string };
  if (!res.ok) {
    const message = 'message' in parsed ? parsed.message : `verify-otp failed (${res.status})`;
    throw new ApiError(res.status, message, parsed);
  }
  const data = (parsed as AuthSuccessResponse).data;
  setAccessToken(data.tokens.accessToken);
  return data;
}

export interface RegisterBody {
  firstName: string;
  lastName: string;
  /** ISO 3166-1 alpha-2 country code (see web/src/lib/countries.ts). */
  country: string;
  /** Optional — omit instead of sending empty string. */
  phone?: string;
  ageConfirmed: true;
  termsAccepted: true;
  termsVersion: string;
  marketingConsent: boolean;
  rulesConsent: {
    weekNumber: number;
    rulesVersion: string;
    clientTs?: string;
  };
}

export interface RegisterResponse {
  status: number;
  data: { user: AuthUser };
}

/**
 * POST /auth/register — authenticated (apiFetch attaches the access token).
 * Promotes PENDING → ACTIVE in a single transaction with profile + rules-
 * consent + terms-acceptance + age + marketing.
 */
export async function postRegister(body: RegisterBody): Promise<AuthUser> {
  const res = await apiJson<RegisterResponse>('/auth/register', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return res.data.user;
}

/**
 * GET /auth/me — fresh profile fetch. Useful when the SPA needs to confirm
 * status after a navigation without rotating tokens.
 */
export async function getMe(): Promise<AuthUser | null> {
  const res = await apiFetch('/auth/me');
  if (res.status === 404 || res.status === 401) return null;
  if (!res.ok) throw new ApiError(res.status, `me failed (${res.status})`);
  const body = (await res.json()) as { status: number; data: { user: AuthUser } };
  return body.data.user;
}
